import React from 'react';
import { ClipboardList } from 'lucide-react';
import { formatDate, hasRealDate } from '../../utils/datetime.js';
import type { PendingFollowup } from '../todos/types.js';
import type { TaskDecision } from './useOpenTasks.js';
import { VisitProductChips } from './VisitProductChips.js';
import { useProducts } from '../../hooks/useOfflineData.js';

interface OpenTasksBannerProps {
  tasks: PendingFollowup[];
  loading: boolean;
  decisions: Record<string, TaskDecision>;
  onChange: (taskId: string, decision: TaskDecision) => void;
  variant: 'compact' | 'full';
}

const ACTION_OPTIONS = [
  { value: 'resolve' as const, label: 'Complete with this visit' },
  { value: 'keep' as const, label: 'Keep open' },
  { value: 'update' as const, label: 'Update' },
];

function priorityClass(priority: string, isOverdue: boolean): string {
  if (isOverdue) return 'bg-red-100 border-red-300 text-red-800';
  if (priority === 'urgent') return 'bg-fuchsia-50 border-fuchsia-300 text-fuchsia-800';
  if (priority === 'high') return 'bg-red-50 border-red-200 text-red-700';
  if (priority === 'medium') return 'bg-orange-50 border-orange-200 text-orange-700';
  return 'bg-blue-50 border-blue-200 text-blue-700';
}

function dueLabel(dueDate: string): string {
  return hasRealDate(dueDate) ? formatDate(dueDate) : 'No date';
}

function datePrefix(value: string | undefined): string {
  return value ? value.substring(0, 10) : '';
}

function humanize(key: string): string {
  return key.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function dueInputValue(task: PendingFollowup, decision?: TaskDecision): string {
  return datePrefix(decision?.due_date ?? task.followup_due_date);
}

function PriorityPill({ task }: { task: PendingFollowup }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold border ${priorityClass(task.followup_priority, task.is_overdue)}`}>
      {task.followup_priority.toUpperCase()}
    </span>
  );
}

function OverdueBadge() {
  return (
    <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold text-[10px]">
      Overdue
    </span>
  );
}

function TaskMeta({ task }: { task: PendingFollowup }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <PriorityPill task={task} />
      <span className="font-medium text-gray-700">Due: {dueLabel(task.followup_due_date)}</span>
      {task.is_overdue && <OverdueBadge />}
      <span className="text-gray-500">· {task.days_since_visit} days old</span>
    </div>
  );
}

function TaskActionSelector({
  task, decision, onChange,
}: {
  task: PendingFollowup;
  decision: TaskDecision;
  onChange: OpenTasksBannerProps['onChange'];
}) {
  const active = decision.action ?? 'resolve';
  const select = (action: TaskDecision['action']) => {
    if (action === 'update') {
      onChange(task.id, {
        action: 'update',
        notes: decision.notes ?? task.followup_notes ?? '',
        priority: decision.priority ?? task.followup_priority,
        due_date: dueInputValue(task, decision),
      });
    } else {
      onChange(task.id, { action });
    }
  };

  return (
    <div className="grid grid-cols-3 gap-2">
      {ACTION_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={active === opt.value}
          onClick={() => select(opt.value)}
          className={`px-2 py-2.5 rounded-lg text-xs font-bold min-h-[48px] transition-all ${
            active === opt.value ? 'bg-amber-500 text-white' : 'bg-white border border-gray-300 text-gray-700'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function UpdateForm({
  task, decision, onChange,
}: {
  task: PendingFollowup;
  decision: TaskDecision;
  onChange: OpenTasksBannerProps['onChange'];
}) {
  const notes = decision.notes ?? task.followup_notes ?? '';
  const priority = decision.priority ?? task.followup_priority;
  const dueDate = dueInputValue(task, decision);
  const emit = (patch: Partial<Pick<TaskDecision, 'notes' | 'priority' | 'due_date'>>) =>
    onChange(task.id, {
      action: 'update',
      notes: patch.notes ?? notes,
      priority: patch.priority ?? priority,
      due_date: patch.due_date ?? dueDate,
    });

  return (
    <div className="mt-3 space-y-3 pt-3 border-t border-amber-200">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => emit({ notes: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
          <select
            value={priority}
            onChange={(e) => emit({ priority: e.target.value as TaskDecision['priority'] })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Due date</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => emit({ due_date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>
    </div>
  );
}

function FullTaskCard({
  task, decision, onChange, products,
}: {
  task: PendingFollowup;
  decision: TaskDecision;
  onChange: OpenTasksBannerProps['onChange'];
  products: Array<{ id: string; name: string }>;
}) {
  const chips = [
    ...task.followup_materials.map((value) => ({ key: `m:${value}`, value })),
    ...task.followup_actions.map((value) => ({ key: `a:${value}`, value })),
  ];
  return (
    <div className={`rounded-xl border p-3 space-y-3 bg-white/80 ${task.is_overdue ? 'border-red-200' : 'border-amber-200'}`}>
      <TaskMeta task={task} />
      <VisitProductChips
        visitProducts={task.visit_products}
        products={products}
        showSecuredBadge
        placementSecured={task.placement_secured}
      />
      <p className="text-xs text-gray-500">Created by {task.created_by_user_name}</p>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span key={chip.key} className="px-2 py-1 bg-white rounded-lg text-xs font-medium text-gray-700 border border-gray-200">
              {humanize(chip.value)}
            </span>
          ))}
        </div>
      )}
      {task.followup_notes && (
        <p className="text-sm text-gray-600 italic border-l-2 border-gray-300 pl-3">
          &ldquo;{task.followup_notes}&rdquo;
        </p>
      )}
      <TaskActionSelector task={task} decision={decision} onChange={onChange} />
      {decision.action === 'update' && (
        <UpdateForm task={task} decision={decision} onChange={onChange} />
      )}
    </div>
  );
}

export const OpenTasksBanner: React.FC<OpenTasksBannerProps> = ({
  tasks, loading, decisions, onChange, variant,
}) => {
  const { products } = useProducts();
  if (loading || tasks.length === 0) return null;

  const header =
    tasks.length === 1 ? '1 open task at this PoS' : `${tasks.length} open tasks at this PoS`;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
      <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
        <ClipboardList size={18} className="flex-shrink-0" aria-hidden />
        <span>{header}</span>
      </div>
      {variant === 'compact' ? (
        <>
          <div className="space-y-2">
            {tasks.map((task) => (
              <div key={task.id} className="space-y-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-900">
                  <PriorityPill task={task} />
                  <span>{dueLabel(task.followup_due_date)}</span>
                  {task.is_overdue && <OverdueBadge />}
                  <span className="text-amber-700">· {task.days_since_visit} days old</span>
                </div>
                <VisitProductChips
                  visitProducts={task.visit_products}
                  products={products}
                  showSecuredBadge
                  placementSecured={task.placement_secured}
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-700">
            You decide how to handle these in the last step.
          </p>
        </>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <FullTaskCard
              key={task.id}
              task={task}
              decision={decisions[task.id] ?? { action: 'resolve' }}
              onChange={onChange}
              products={products}
            />
          ))}
        </div>
      )}
    </div>
  );
};
